// components/composant_navbar/FormContact.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../../CSS/form_contact.css';

const FormContact = () => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [recaptchaReady, setRecaptchaReady] = useState(false);
    const recaptchaContainerRef = useRef(null);
    const recaptchaWidgetIdRef = useRef(null);

    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        email: '',
        phone: '',
        message: '',
        objet: '',
        autresPreoccupation: '',
        pays: ''
    });

    // Récupérer la clé reCAPTCHA depuis .env
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    // Vérification stricte
    if (!recaptchaSiteKey) {
        console.error('❌ REACT_APP_RECAPTCHA_SITE_KEY n\'est pas définie dans le fichier .env');
    }

    // Fonctions globales pour reCAPTCHA
    useEffect(() => {
        // Nettoyer les fonctions globales précédentes
        const handleSuccess = (token) => {
            console.log('✅ reCAPTCHA validé');
            setRecaptchaToken(token);
        };

        const handleExpired = () => {
            console.log('⚠️ reCAPTCHA expiré');
            setRecaptchaToken('');
        };

        const handleError = () => {
            console.log('❌ Erreur reCAPTCHA');
            setRecaptchaToken('');
        };

        window.handleRecaptchaSuccess = handleSuccess;
        window.handleRecaptchaExpired = handleExpired;
        window.handleRecaptchaError = handleError;

        return () => {
            // Nettoyer les fonctions globales lors du démontage
            delete window.handleRecaptchaSuccess;
            delete window.handleRecaptchaExpired;
            delete window.handleRecaptchaError;
        };
    }, []);

    // Charger reCAPTCHA dynamiquement
    useEffect(() => {
        const scriptId = 'recaptcha-script';

        // Vérifier si le script est déjà chargé
        const checkRecaptchaLoaded = () => {
            if (window.grecaptcha && window.grecaptcha.render) {
                console.log('✅ reCAPTCHA déjà chargé');
                setRecaptchaReady(true);
                return true;
            }
            return false;
        };

        // Si déjà chargé, on active directement
        if (checkRecaptchaLoaded()) {
            return;
        }

        // Vérifier si le script existe déjà dans le DOM
        const existingScript = document.getElementById(scriptId);
        if (existingScript) {
            // Attendre que le script soit chargé
            const checkInterval = setInterval(() => {
                if (window.grecaptcha && window.grecaptcha.render) {
                    setRecaptchaReady(true);
                    clearInterval(checkInterval);
                }
            }, 100);
            return () => clearInterval(checkInterval);
        }

        // Charger le script
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://www.google.com/recaptcha/api.js?hl=fr&onload=onRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;

        // Définir la fonction de callback global
        window.onRecaptchaLoad = () => {
            console.log('✅ reCAPTCHA script chargé');
            setRecaptchaReady(true);
        };

        document.head.appendChild(script);

        // Nettoyage
        return () => {
            // Ne pas supprimer le script car pourrait être utilisé par d'autres instances
            // Mais on garde pour le nettoyage
        };
    }, []);

    // Fonction pour rendre ou réinitialiser reCAPTCHA
    const renderRecaptcha = () => {
        if (!recaptchaReady || !recaptchaContainerRef.current || !window.grecaptcha) {
            return;
        }

        try {
            // Si un widget existe déjà, le réinitialiser
            if (recaptchaWidgetIdRef.current !== null) {
                console.log('🔄 Réinitialisation du reCAPTCHA existant');
                window.grecaptcha.reset(recaptchaWidgetIdRef.current);
                setRecaptchaToken('');
                return;
            }

            // Nettoyer le conteneur avant de rendre
            while (recaptchaContainerRef.current.firstChild) {
                recaptchaContainerRef.current.removeChild(recaptchaContainerRef.current.firstChild);
            }

            // Rendre reCAPTCHA explicitement
            const widgetId = window.grecaptcha.render(recaptchaContainerRef.current, {
                'sitekey': recaptchaSiteKey,
                'callback': 'handleRecaptchaSuccess',
                'expired-callback': 'handleRecaptchaExpired',
                'error-callback': 'handleRecaptchaError',
                'theme': 'light',
                'size': 'normal'
            });

            console.log('✅ reCAPTCHA rendu avec succès, widget ID:', widgetId);
            recaptchaWidgetIdRef.current = widgetId;
        } catch (error) {
            console.error('❌ Erreur lors du rendu reCAPTCHA:', error);
        }
    };

    // Rendre reCAPTCHA quand le conteneur est prêt
    useEffect(() => {
        // Réinitialiser l'ID du widget lors du montage du composant
        recaptchaWidgetIdRef.current = null;

        // Petit délai pour s'assurer que le DOM est prêt
        const timer = setTimeout(() => {
            renderRecaptcha();
        }, 100);

        return () => {
            clearTimeout(timer);
            // Nettoyer le widget lors du démontage
            if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
                try {
                    // Pour reCAPTCHA v2, on ne peut pas détruire, mais on peut réinitialiser
                    window.grecaptcha.reset(recaptchaWidgetIdRef.current);
                    recaptchaWidgetIdRef.current = null;
                } catch (error) {
                    console.error('Erreur lors du nettoyage reCAPTCHA:', error);
                }
            }
        };
    }, [recaptchaReady, recaptchaSiteKey]);

    // Re-rendre le reCAPTCHA si le conteneur change
    useEffect(() => {
        if (recaptchaReady && recaptchaContainerRef.current) {
            renderRecaptcha();
        }
    }, [recaptchaReady, recaptchaContainerRef.current]);

    const resetRecaptcha = () => {
        if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
            try {
                window.grecaptcha.reset(recaptchaWidgetIdRef.current);
                setRecaptchaToken('');
                console.log('🔄 reCAPTCHA réinitialisé');
            } catch (error) {
                console.error('Erreur lors de la réinitialisation reCAPTCHA:', error);
                // En cas d'erreur, forcer un re-rendu
                recaptchaWidgetIdRef.current = null;
                setTimeout(() => renderRecaptcha(), 100);
            }
        } else {
            // Si pas de widget, en créer un nouveau
            setTimeout(() => renderRecaptcha(), 100);
        }
    };

    // Options pour le select objet
    const objetOptions = [
        { value: "RAVIP", label: "RAVIP" },
        { value: "Carte consulaire", label: "Carte consulaire" },
        { value: "Passeport", label: "Passeport" },
        { value: "Légalisation", label: "Légalisation" },
        { value: "Certification", label: "Certification" },
        { value: "Mariage", label: "Mariage" },
        { value: "Autres préoccupations", label: "Autres préoccupations", hasInput: true }
    ];

    // Options pour le select pays
    const paysOptions = [
        { value: "Bangladesh", label: "Bangladesh" },
        { value: "Bhoutan", label: "Bhoutan" },
        { value: "Cambodge", label: "Cambodge" },
        { value: "Chine", label: "Chine" },
        { value: "Inde", label: "Inde" },
        { value: "Laos", label: "Laos" },
        { value: "Mongolie", label: "Mongolie" },
        { value: "Népal", label: "Népal" },
        { value: "République de Corée", label: "République de Corée" },
        { value: "République populaire démocratique de Corée", label: "République populaire démocratique de Corée" },
        { value: "Sri Lanka", label: "Sri Lanka" },
        { value: "Thaïlande", label: "Thaïlande" },
        { value: "Vietnam", label: "Vietnam" }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setFormData(prev => ({ ...prev, phone: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Vérification reCAPTCHA
        if (!recaptchaToken) {
            alert("Veuillez valider le reCAPTCHA");
            return;
        }

        let objetFinal = formData.objet;
        if (formData.objet === "Autres préoccupations") {
            if (!formData.autresPreoccupation.trim()) {
                alert("Veuillez préciser votre préoccupation");
                return;
            }
            objetFinal = `Autres préoccupations: ${formData.autresPreoccupation}`;
        }

        if (!formData.nom || !formData.prenom || !formData.email || !formData.phone || !formData.objet || !formData.pays) {
            alert("Veuillez remplir tous les champs obligatoires");
            return;
        }

        setIsSubmitting(true);

        try {
            const formPayload = {
                "code-ambassade": "uuidARBC0A4",
                "lastname": formData.nom,
                "firstname": formData.prenom,
                "email": formData.email,
                "phone": formData.phone,
                "object": objetFinal,
                "content": formData.message || "Demande de rendez-vous",
                "town": "Benin",
                "themeDiscuss": "",
                "country": formData.pays,
                "acceptDataTreat": true
            };

            const response = await fetch(`${baseUrl}/api/v1/embassy/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formPayload)
            });

            const result = await response.json();

            if (response.ok && result.response_code === 200) {
                alert(result.message || "Votre demande a été envoyée avec succès !");
                // Réinitialiser le formulaire
                setFormData({
                    nom: '',
                    prenom: '',
                    email: '',
                    phone: '',
                    message: '',
                    objet: '',
                    autresPreoccupation: '',
                    pays: ''
                });
                // Réinitialiser le champ "autres préoccupations"
                setFormData(prev => ({ ...prev, autresPreoccupation: '' }));
                // Réinitialiser reCAPTCHA
                resetRecaptcha();
            } else {
                throw new Error(result.message || "Erreur lors de l'envoi de la demande");
            }

        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            alert(error.message || "Une erreur est survenue. Veuillez réessayer plus tard.");
            resetRecaptcha();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="form-contact-container">
            <div className="form-contact-header">
                <h1>Formulaire de contact</h1>
            </div>

            <div className="form-contact-wrapper">
                <form className="contact-form" onSubmit={handleSubmit}>
                    {/* Ligne 1: Nom et Prénom */}
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="nom">Nom *</label>
                            <input
                                type="text"
                                id="nom"
                                name="nom"
                                placeholder="Votre nom"
                                className="form-input"
                                value={formData.nom}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="prenom">Prénom *</label>
                            <input
                                type="text"
                                id="prenom"
                                name="prenom"
                                placeholder="Votre prénom"
                                className="form-input"
                                value={formData.prenom}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    </div>

                    {/* Ligne 2: Pays de résidence et Objet de la demande */}
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="pays">Pays de résidence *</label>
                            <select
                                id="pays"
                                name="pays"
                                className="form-select"
                                value={formData.pays}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="" disabled>Sélectionnez un pays *</option>
                                {paysOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="objet">Objet de la demande *</label>
                            <select
                                id="objet"
                                name="objet"
                                className="form-select"
                                value={formData.objet}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="" disabled>Sélectionnez un objet *</option>
                                {objetOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Champ conditionnel pour "Autres préoccupations" */}
                    {formData.objet === "Autres préoccupations" && (
                        <div className="form-group full-width">
                            <label htmlFor="autresPreoccupation">Précisez votre préoccupation *</label>
                            <input
                                type="text"
                                id="autresPreoccupation"
                                name="autresPreoccupation"
                                placeholder="Veuillez préciser"
                                className="form-input"
                                value={formData.autresPreoccupation}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                    )}

                    {/* Ligne 3: Email et Téléphone */}
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                placeholder="votre@email.com"
                                className="form-input"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">Téléphone *</label>
                            <div className="phone-input-wrapper">
                                <PhoneInput
                                    country={'cn'}
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    inputProps={{
                                        name: 'phone',
                                        required: true,
                                        placeholder: 'Numéro de téléphone'
                                    }}
                                    preferredCountries={['cn','bj', 'fr', 'us', 'ca', 'gb']}
                                    enableSearch={true}
                                    searchPlaceholder="Rechercher un pays"
                                    enableAreaCodes={false}
                                    enableLongNumbers={true}
                                    autoFormat={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Ligne 4: Message (pleine largeur) */}
                    <div className="form-group full-width">
                        <label htmlFor="message">Message</label>
                        <textarea
                            id="message"
                            name="message"
                            className="form-textarea"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Votre message (précisez votre demande si nécessaire)"
                            rows="5"
                        />
                    </div>

                    {/* Container reCAPTCHA - Version corrigée */}
                    <div className="recaptcha-container">
                        {!recaptchaReady && (
                            <div style={{ padding: '10px', textAlign: 'center' }}>
                                Chargement du reCAPTCHA...
                            </div>
                        )}
                        <div
                            ref={recaptchaContainerRef}
                            key={`recaptcha-${recaptchaReady}`} // Force le re-rendu
                        ></div>
                    </div>

                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={
                            isSubmitting ||
                            !recaptchaToken ||
                            !formData.nom.trim() ||
                            !formData.prenom.trim() ||
                            !formData.email.trim() ||
                            !formData.phone.trim() ||
                            !formData.objet ||
                            !formData.pays ||
                            (formData.objet === "Autres préoccupations" && !formData.autresPreoccupation.trim())
                        }
                    >
                        {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default FormContact;